// Utilities for safe date parsing/formatting respecting user timezone

export const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

// Parse a value that can be ISO datetime or YYYY-MM-DD date-only
export const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const m = /^\d{4}-\d{2}-\d{2}$/.exec(value);
  if (m) {
    const [y, mo, d] = value.split('-').map(Number);
    return new Date(y, mo - 1, d); // local time midnight
  }
  return new Date(value);
};

// Format date-like value in user's timezone
export const formatDate = (value, options = {}) => {
  const date = parseDateValue(value);
  if (!date || isNaN(date.getTime())) return '';
  const tz = options.timeZone || getUserTimeZone();
  return date.toLocaleDateString(undefined, { 
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Format time given a base date (YYYY-MM-DD or Date) and a time string (HH:mm)
export const formatTime = (dateValue, timeString, options = {}) => {
  if (!dateValue || !timeString) return '';
  const base = parseDateValue(dateValue);
  if (!base || isNaN(base.getTime())) return '';
  const [hh, mm] = timeString.split(':').map(Number);
  const dt = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh || 0, mm || 0, 0, 0);
  const tz = options.timeZone || getUserTimeZone();
  return dt.toLocaleTimeString(undefined, { timeZone: tz, hour: '2-digit', minute: '2-digit' });
};

// Map timezone to shortform abbreviation
export const getTimezoneAbbreviation = (timezone) => {
  if (!timezone) return '';
  
  const timezoneMap = {
    // North America
    'America/New_York': 'EST/EDT',
    'America/Chicago': 'CST/CDT',
    'America/Denver': 'MST/MDT',
    'America/Los_Angeles': 'PST/PDT',
    'America/Anchorage': 'AKST/AKDT',
    'America/Phoenix': 'MST',
    'America/Toronto': 'EST/EDT',
    'America/Vancouver': 'PST/PDT',
    'America/Mexico_City': 'CST/CDT',
    
    // Europe
    'Europe/London': 'GMT/BST',
    'Europe/Paris': 'CET/CEST',
    'Europe/Berlin': 'CET/CEST',
    'Europe/Rome': 'CET/CEST',
    'Europe/Madrid': 'CET/CEST',
    'Europe/Amsterdam': 'CET/CEST',
    'Europe/Brussels': 'CET/CEST',
    'Europe/Vienna': 'CET/CEST',
    'Europe/Warsaw': 'CET/CEST',
    'Europe/Prague': 'CET/CEST',
    'Europe/Budapest': 'CET/CEST',
    'Europe/Athens': 'EET/EEST',
    'Europe/Helsinki': 'EET/EEST',
    'Europe/Istanbul': 'TRT',
    'Europe/Moscow': 'MSK',
    'Europe/Zurich': 'CET/CEST',
    'Europe/Stockholm': 'CET/CEST',
    'Europe/Copenhagen': 'CET/CEST',
    'Europe/Oslo': 'CET/CEST',
    'Europe/Dublin': 'GMT/IST',
    'Europe/Lisbon': 'WET/WEST',
    
    // Asia
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Asia/Hong_Kong': 'HKT',
    'Asia/Singapore': 'SGT',
    'Asia/Seoul': 'KST',
    'Asia/Dubai': 'GST',
    'Asia/Kolkata': 'IST',
    'Asia/Mumbai': 'IST',
    'Asia/Delhi': 'IST',
    'Asia/Bangkok': 'ICT',
    'Asia/Jakarta': 'WIB',
    'Asia/Manila': 'PST',
    'Asia/Taipei': 'CST',
    'Asia/Tehran': 'IRST',
    'Asia/Jerusalem': 'IST/IDT',
    'Asia/Karachi': 'PKT',
    'Asia/Dhaka': 'BST',
    
    // Australia & Pacific
    'Australia/Sydney': 'AEDT/AEST',
    'Australia/Melbourne': 'AEDT/AEST',
    'Australia/Brisbane': 'AEST',
    'Australia/Perth': 'AWST',
    'Australia/Adelaide': 'ACDT/ACST',
    'Pacific/Auckland': 'NZDT/NZST',
    'Pacific/Fiji': 'FJT',
    'Pacific/Honolulu': 'HST',
    
    // South America
    'America/Sao_Paulo': 'BRT',
    'America/Buenos_Aires': 'ART',
    'America/Santiago': 'CLT/CLST',
    'America/Bogota': 'COT',
    'America/Lima': 'PET',
    
    // Africa
    'Africa/Cairo': 'EET',
    'Africa/Johannesburg': 'SAST',
    'Africa/Nairobi': 'EAT',
    'Africa/Lagos': 'WAT',
    'Africa/Casablanca': 'WET',
    
    // UTC
    'UTC': 'UTC',
    'Etc/UTC': 'UTC',
  };
  
  return timezoneMap[timezone] || timezone;
};
